import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, Injectable, Signal } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { groupBy } from 'lodash';
import { Observable, of, switchMap } from 'rxjs';
import {
  EItemTimingStatus,
  IItemTimingStep,
  IItemTimingWorkInstruction,
} from '../../../../shared/services/item-timing/item-timing.interface';
import { ItemTimingService } from '../../../../shared/services/item-timing/item-timing.service';
import { WorkInstructionStatusService } from '../../../../shared/services/work-instruction-status/work-instruction-status.service';
import { ELoadStatus } from '../../../../state/state.interface';
import { IBaseResponse } from '../../../../utilities/http-utilities.service';
import {
  ICheckIn,
  IWorkInstructionCheckInStep,
} from '../work-instruction-check-in/work-instruction-check-in.interface';
import {
  IDeleteItemWorkInstructionAssignment,
  IWorkInstructionCheckInRow,
  IWorkInstructionItemCard,
} from './link-work-instruction.interface';

export interface ILinkWorkInstructionState {
  assignmentLoading: ELoadStatus;
  dataLoading: ELoadStatus;
  workInstructions: IWorkInstructionItemCard[];
  checkIns: ICheckIn[];
}

@Injectable()
export class LinkWorkInstructionStore extends ComponentStore<ILinkWorkInstructionState> {
  public readonly data$: Signal<IWorkInstructionItemCard[]> = this.selectSignal(
    (state: ILinkWorkInstructionState) => state.workInstructions ?? [],
  );
  private readonly checkIns: Signal<ICheckIn[]> = this.selectSignal(
    (state: ILinkWorkInstructionState) => state.checkIns,
  );
  public readonly rows: Signal<IWorkInstructionCheckInRow[]> = computed(() => {
    const checkInsByAssignmentId: Record<string, ICheckIn[]> = groupBy(this.checkIns(), 'itemGuideAssignmentId');

    return this.data$().map((workInstruction: IWorkInstructionItemCard) => ({
      ...workInstruction,
      checkIns: checkInsByAssignmentId[workInstruction.assignmentId] ?? [],
    }));
  });

  readonly loadWorkInstructions = this.effect((trigger$: Observable<string>) =>
    trigger$.pipe(
      switchMap((itemKey: string) => {
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.itemTimingService.getItemTimings(itemKey).pipe(
          tapResponse(
            (response) => {
              // The API already orders these: started work instructions by start time, then the rest by their
              // configured link order.
              const workInstructions: IWorkInstructionItemCard[] = response.data.workInstructions.map(
                (workInstruction: IItemTimingWorkInstruction) => this.toCard(workInstruction),
              );

              this.patchState({ dataLoading: ELoadStatus.success, workInstructions });
              this.loadCheckIns();
            },
            (error) => {
              this.patchState({ checkIns: [], dataLoading: ELoadStatus.failure, workInstructions: [] });

              // eslint-disable-next-line no-console
              console.error('Error loading work instruction timings', error);
            },
          ),
        );
      }),
    ),
  );

  readonly loadCheckIns = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      switchMap(() => {
        const assignmentIds: number[] = this.get().workInstructions.map(
          (workInstruction: IWorkInstructionItemCard) => workInstruction.assignmentId,
        );

        if (!assignmentIds.length) {
          this.patchState({ checkIns: [] });

          return of(null);
        }

        return this.getOngoingCheckIns(assignmentIds).pipe(
          tapResponse(
            (response: IBaseResponse<ICheckIn[]>) => this.patchState({ checkIns: response.data }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading check-ins', error),
          ),
        );
      }),
    ),
  );

  readonly unassignWorkInstructions = this.effect((trigger$: Observable<IDeleteItemWorkInstructionAssignment>) =>
    trigger$.pipe(
      switchMap(({ id, startDateForRecurringUpdate }) => {
        this.patchState({ assignmentLoading: ELoadStatus.loading });
        let httpParams = new HttpParams();

        if (startDateForRecurringUpdate !== undefined) {
          httpParams = httpParams.set('startDateForRecurringUpdate', String(startDateForRecurringUpdate));
        }

        return this.deleteItemGuideAssignment(id, { params: httpParams }).pipe(
          tapResponse(
            () => {
              this.patchState({ assignmentLoading: ELoadStatus.success });
            },
            () => {
              this.patchState({ assignmentLoading: ELoadStatus.failure });
            },
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly itemTimingService: ItemTimingService,
    private readonly workInstructionStatusService: WorkInstructionStatusService,
  ) {
    super({
      assignmentLoading: ELoadStatus.initial,
      checkIns: [],
      dataLoading: ELoadStatus.initial,
      workInstructions: [],
    });
  }

  private toCard(workInstruction: IItemTimingWorkInstruction): IWorkInstructionItemCard {
    return {
      assignmentId: workInstruction.itemGuideAssignmentId,
      checkInSteps: this.getCheckInSteps(workInstruction),
      id: workInstruction.guideId,
      name: workInstruction.name,
      workInstructionStatus: this.workInstructionStatusService.getWorkInstructionStatus(workInstruction),
    };
  }

  /** Steps that can be checked in to: started (they have a guide submission) and not completed, in step order. */
  private getCheckInSteps(workInstruction: IItemTimingWorkInstruction): IWorkInstructionCheckInStep[] {
    return workInstruction.steps
      .filter((step: IItemTimingStep) => step.guideSubmissionId !== null && step.status !== EItemTimingStatus.completed)
      .map((step: IItemTimingStep) => ({ guideSubmissionId: step.guideSubmissionId!, name: step.name }));
  }

  private getOngoingCheckIns(assignmentIds: number[]): Observable<IBaseResponse<ICheckIn[]>> {
    const httpParams: HttpParams = new HttpParams().set('itemGuideAssignmentIds', assignmentIds.join(','));

    return this.http.get<IBaseResponse<ICheckIn[]>>('check-ins/ongoing', { params: httpParams });
  }

  private deleteItemGuideAssignment(
    id: number,
    httpOptions: Parameters<HttpClient['get']>[1] = {},
  ): Observable<IBaseResponse<unknown>> {
    return this.http.delete<IBaseResponse<unknown>>(`item-guide-assignments/${id}`, httpOptions);
  }
}
